// Service Context - React Context for Dependency Injection
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getConfig } from '../config/index.js';

/**
 * Service Context for dependency injection in React components
 * Provides access to all registered services throughout the component tree
 */
const ServiceContext = createContext(null);

/**
 * Service Provider Component
 * Initializes and provides services to child components
 */
const ServiceProvider = ({ children }) => {
  const [services, setServices] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Shared wallet state for all components
  const [walletState, setWalletState] = useState({
    isConnected: false,
    address: null,
    publicKey: null,
    isConnecting: false,
    error: null
  });
  
  const config = getConfig();

  useEffect(() => {
    const initializeServices = async () => {
      try {
        if (config.ENABLE_DEBUG_LOGS) {
          console.log('🚀 Initializing Service Context...');
        }

        // Dynamic import to avoid circular dependency
        const { default: serviceFactory } = await import('../services/ServiceFactory.js');
        await serviceFactory.initialize();
        setServices(serviceFactory);
        setError(null);

        if (config.ENABLE_DEBUG_LOGS) {
          console.log('✅ Service Context initialized successfully');
          console.log('📋 Registered services:', serviceFactory.getRegisteredServices());
        }

      } catch (err) {
        console.error('❌ Failed to initialize Service Context:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    initializeServices();
  }, [config.ENABLE_DEBUG_LOGS]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="service-loading">
        <div className="loading-spinner">⏳</div>
        <p>Initializing services...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="service-error">
        <div className="error-icon">❌</div>
        <h3>Service Initialization Error</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>
          Reload Application
        </button>
      </div>
    );
  }

  return (
    <ServiceContext.Provider value={{ services, walletState, setWalletState }}>
      {children}
    </ServiceContext.Provider>
  );
};

/**
 * Hook to access the service factory
 * @returns {ServiceFactory} Service factory instance
 */
export const useServices = () => {
  const context = useContext(ServiceContext);
  
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  
  return context.services;
};

/**
 * Hook to access the wallet service
 * @returns {IWalletService} Wallet service instance
 */
export const useWalletService = () => {
  const services = useServices();
  return services.getService('walletService');
};

/**
 * Hook to access shared wallet state
 */
export const useWalletState = () => {
  const context = useContext(ServiceContext);
  
  if (!context) {
    throw new Error('useWalletState must be used within a ServiceProvider');
  }
  
  return { walletState: context.walletState, setWalletState: context.setWalletState };
};

/**
 * Hook to access any service by name
 * @param {string} serviceName - Name of the service to get
 * @returns {any} Service instance
 */
export const useService = (serviceName) => {
  const services = useServices();
  return services.getService(serviceName);
};

/**
 * Hook for wallet connection state management
 * Provides wallet connection state and methods
 */
export const useWallet = () => {
  const walletService = useWalletService();
  const { walletState, setWalletState } = useWalletState();
  const { isConnected, address, publicKey, isConnecting, error } = walletState;
  
  // Helper functions to update wallet state
  const updateWalletState = (updates) => {
    setWalletState(prev => ({ ...prev, ...updates }));
  };

  /**
   * Connect to wallet - provider-agnostic, works with any Solana wallet
   * @returns {Promise<{success: boolean, address?: string, error?: string}>}
   */
  const connect = async () => {
    updateWalletState({ isConnecting: true, error: null });

    try {
      // Let wallet service auto-detect available wallet
      const result = await walletService.connect();
      
      console.log('🔍 Wallet service connect result:', result);
      
      if (result.success) {
        console.log('✅ Setting wallet state:', { address: result.address, publicKey: result.publicKey });
        updateWalletState({
          isConnected: true,
          address: result.address,
          publicKey: result.publicKey,
          isConnecting: false,
          error: null
        });
        
        // Add a small delay to ensure state updates
        setTimeout(() => {
          console.log('📊 Final wallet state after update:', { isConnected: true, address: result.address });
        }, 100);
      } else {
        console.log('❌ Wallet connection failed:', result.error);
        updateWalletState({ error: result.error, isConnecting: false });
      }
      
      return result;
    } catch (err) {
      updateWalletState({ error: err.message, isConnecting: false });
      const errorResult = { success: false, error: err.message };
      return errorResult;
    }
  };

  /**
   * Disconnect from wallet
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const disconnect = async () => {
    try {
      const result = await walletService.disconnect();
      
      // Always reset state regardless of result
      updateWalletState({
        isConnected: false,
        address: null,
        publicKey: null,
        error: null,
        isConnecting: false
      });
      
      return result;
    } catch (err) {
      // Still reset state even if disconnect fails
      updateWalletState({
        isConnected: false,
        address: null,
        publicKey: null,
        error: err.message,
        isConnecting: false
      });
      
      return { success: false, error: err.message };
    }
  };

  /**
   * Sign a transaction
   * @param {any} transaction - Transaction to sign
   */
  const signTransaction = async (transaction) => {
    if (!isConnected) {
      throw new Error('Wallet not connected');
    }
    return await walletService.signTransaction(transaction);
  };

  /**
   * Sign multiple transactions
   * @param {any[]} transactions - Transactions to sign
   */
  const signAllTransactions = async (transactions) => {
    if (!isConnected) {
      throw new Error('Wallet not connected');
    }
    return await walletService.signAllTransactions(transactions);
  };

  return {
    // State
    isConnected,
    address,
    publicKey,
    isConnecting,
    error,
    
    // Methods
    connect,
    disconnect,
    signTransaction,
    signAllTransactions,
    
    // Utility
    isWalletAvailable: walletService.isWalletAvailable.bind(walletService),
    getSupportedWallets: walletService.getSupportedWallets.bind(walletService),
  };
};

// Export ServiceProvider at the end to avoid hoisting issues
export { ServiceProvider };
